/**
 * When to ask a customer to review a recent delivery. Pure predicate plus
 * thin storage helpers, mirroring utils/passkeyPrompt.ts so the decision is
 * testable without rendering anything.
 */

/** How long "Not now" holds the prompt off for. */
export const SNOOZE_DAYS = 30;
/** Only deliveries this recent are worth asking about. */
export const DELIVERED_WINDOW_DAYS = 30;

const KEY_PREFIX = 'review_prompt_snoozed_until';

export const reviewSnoozeKey = (userId?: number | string) => `${KEY_PREFIX}_${userId}`;

export interface DeliveredPackageSummary {
  id: number;
  trackingNo?: string;
  description?: string;
  deliveredDate?: string | Date | null;
}

export interface ReviewPromptInput {
  isReviewsEnabled?: boolean;
  deliveredPackages: DeliveredPackageSummary[];
  /** Package ids the customer has already reviewed (any status). */
  reviewedPackageIds: number[];
  snoozedUntil: Date | null;
  now: Date;
}

function deliveredWithinWindow(pkg: DeliveredPackageSummary, now: Date): boolean {
  if (!pkg.deliveredDate) return false;
  const delivered = new Date(pkg.deliveredDate);
  if (Number.isNaN(delivered.getTime())) return false;
  const ageMs = now.getTime() - delivered.getTime();
  return ageMs >= 0 && ageMs <= DELIVERED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

/** Newest delivered package within the window that has not been reviewed yet. */
export const pickPackageToReview = ({
  deliveredPackages,
  reviewedPackageIds,
  now,
}: Pick<ReviewPromptInput, 'deliveredPackages' | 'reviewedPackageIds' | 'now'>): DeliveredPackageSummary | null => {
  const candidates = deliveredPackages
    .filter((pkg) => pkg.id && !reviewedPackageIds.includes(pkg.id) && deliveredWithinWindow(pkg, now))
    .sort((a, b) => new Date(b.deliveredDate as string).getTime() - new Date(a.deliveredDate as string).getTime());
  return candidates[0] || null;
};

export const shouldPromptReview = (input: ReviewPromptInput): boolean => {
  if (!input.isReviewsEnabled) return false;
  if (input.snoozedUntil && input.snoozedUntil > input.now) return false;
  return pickPackageToReview(input) !== null;
};

export const readSnooze = (userId?: number | string): Date | null => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(reviewSnoozeKey(userId));
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

export const writeSnooze = (userId?: number | string): void => {
  if (!userId) return;
  try {
    const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000);
    localStorage.setItem(reviewSnoozeKey(userId), until.toISOString());
  } catch {
    // A blocked localStorage just means we ask again next time.
  }
};

export const clearSnooze = (userId?: number | string): void => {
  if (!userId) return;
  try {
    localStorage.removeItem(reviewSnoozeKey(userId));
  } catch {
    // Nothing to do.
  }
};
